import React, { useState, useEffect, useMemo } from 'react';
import { 
  Heart, 
  ShieldCheck, 
  Building2, 
  User, 
  Search, 
  Sparkles, 
  ArrowUpRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SPONSORS_DATA } from '../data/sponsorsData';
import { getExtractedSponsorLogos } from '../lib/sponsorExtractor';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';

export default function TakTil() {
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'company', 'person'
  const [searchQuery, setSearchQuery] = useState('');
  const [logoMap, setLogoMap] = useState({});
  const [, setIsLoadingLogos] = useState(true);

  const { data: rawSponsors } = useCmsCollection('sponsorer', SPONSORS_DATA);

  // Normalize CMS or local sponsors data and map fallback logos/fileKeys
  const sponsorsList = useMemo(() => {
    return rawSponsors.map((s, idx) => {
      const d = s.data || s;
      const name = d.navn || d.name || '';
      const fallback = SPONSORS_DATA.find(
        (sp) => sp.name?.toLowerCase().trim() === name.toLowerCase().trim() || sp.id === (d.id || s.id)
      );
      return {
        id: d.id || s.id || fallback?.id || `sponsor-${idx}`,
        name: name,
        subtitle: d.undertitel || d.subtitle || fallback?.subtitle || '',
        year: d.aar_tekst || d.year || fallback?.year || '',
        isLatest: d.er_seneste !== undefined ? d.er_seneste : (d.isLatest ?? fallback?.isLatest ?? false),
        fileKey: d.fileKey || fallback?.fileKey || null,
        type: d.type_tekst || d.type || fallback?.type || 'company',
        logo: Array.isArray(d.logo) ? d.logo[0] : (d.logo || fallback?.logo || null),
      };
    });
  }, [rawSponsors]);

  // Load logos extracted from sponsor.docx
  useEffect(() => {
    let mounted = true;
    async function loadLogos() {
      try {
        const map = await getExtractedSponsorLogos();
        if (mounted) {
          setLogoMap(map);
          setIsLoadingLogos(false);
        }
      } catch (err) {
        console.error('Kunne ikke hente sponsor-logoer:', err);
        if (mounted) setIsLoadingLogos(false);
      }
    }
    loadLogos();
    return () => { mounted = false; };
  }, []);

  // Filtered sponsors (search + optional type filter)
  const filteredSponsors = useMemo(() => {
    return sponsorsList.filter(sponsor => {
      // Type filter
      if (activeFilter !== 'all' && sponsor.type !== activeFilter) {
        return false;
      }
      // Search query (matches name or subtitle)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchName = sponsor.name?.toLowerCase().includes(query);
        const matchSub = sponsor.subtitle?.toLowerCase().includes(query);
        return matchName || matchSub;
      }
      return true;
    });
  }, [sponsorsList, activeFilter, searchQuery]);

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <SEO
        title="Sponsorer & Støtter | Tak til dem der støtter TFDI"
        description="Se de fantastiske virksomheder, fonde og privatpersoner, der støtter Tak For Din Indsats og muliggør vores hjælp til Danmarks frontpersonale."
      />

      {/* Hero Section */}
      <div className="relative bg-slate-900 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.18),transparent_55%)]" />
        <div className="absolute -left-10 -bottom-10 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold mb-5 shadow-sm">
            <Heart className="w-4 h-4 fill-current" />
            <span>Vores uvurderlige støtter</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight mb-4">
            Tak til dem der allerede støtter <span className="text-red-500">TFDI</span>
          </h1>
          <p className="max-w-2xl mx-auto text-slate-300 text-base md:text-lg">
            Virksomheder, fonde og ildsjæle, der yder en afgørende indsats for veteraner, beredskab og deres pårørende.
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16 space-y-8">

        {/* Intro Banner: Aktivt samfundsansvar */}
        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-3xl">
            <div className="flex items-center gap-2 text-red-600 font-bold text-xs uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4" />
              <span>Aktivt samfundsansvar</span>
            </div>
            <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
              De virksomheder og organisationer, du ser her på siden, har valgt at tage et aktivt samfundsansvar. De har rakt hånden ud og bidraget økonomisk, praktisk eller med ressourcer for at støtte vores sag.
            </p>
          </div>
          <Link
            to="/samarbejde"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
          >
            <span>Bliv sponsor</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
          {/* Quick Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søg efter sponsor..."
              className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-slate-800 placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-3">
            {/* Type Filter Buttons */}
            <div className="flex items-center p-1 bg-slate-100 rounded-xl text-xs font-medium">
              <button
                onClick={() => setActiveFilter('all')}
                className={`py-1.5 px-3 rounded-lg transition-all ${
                  activeFilter === 'all' 
                    ? 'bg-white text-slate-900 font-bold shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Alle ({sponsorsList.length})
              </button>
              <button
                onClick={() => setActiveFilter('company')}
                className={`py-1.5 px-3 rounded-lg transition-all flex items-center gap-1.5 ${
                  activeFilter === 'company' 
                    ? 'bg-white text-slate-900 font-bold shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <span>Virksomheder</span>
              </button>
              <button
                onClick={() => setActiveFilter('person')}
                className={`py-1.5 px-3 rounded-lg transition-all flex items-center gap-1.5 ${
                  activeFilter === 'person' 
                    ? 'bg-white text-slate-900 font-bold shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <User className="w-3.5 h-3.5" />
                <span>Personer</span>
              </button>
            </div>
          </div>
        </div>

        {/* Sponsors Cards Grid */}
        {filteredSponsors.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
              <Search className="w-6 h-6" />
            </div>
            <h4 className="text-base font-bold text-slate-900">Ingen sponsorer fundet</h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Prøv at søge efter et andet navn eller nulstille filtreringen.
            </p>
            <button
              onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition-colors cursor-pointer"
            >
              Nulstil søgning
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredSponsors.map((sponsor) => {
              const logoSrc = sponsor.logo || (sponsor.fileKey ? logoMap[sponsor.fileKey] : null);

              return (
                <div
                  key={sponsor.id}
                  className="group bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
                >
                  <div>
                    {/* Top row: Kun årstallet oppe i højre hjørne som eneste i toppen */}
                    <div className="flex justify-end mb-3">
                      {sponsor.year && (
                        <span className="text-xs font-semibold text-slate-400 font-mono tracking-tight">
                          {sponsor.year}
                        </span>
                      )}
                    </div>

                    {/* LOGO - rent og centreret i boks */}
                    <div className="h-28 rounded-xl bg-slate-50/70 border border-slate-100 flex items-center justify-center p-3 mb-4 group-hover:bg-white group-hover:border-slate-200 transition-all overflow-hidden">
                      {logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={sponsor.name}
                          className="max-h-full max-w-full object-contain filter group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="text-center">
                          <div className="w-10 h-10 rounded-full bg-red-50 border border-red-100 text-red-600 flex items-center justify-center mx-auto mb-1 shadow-2xs">
                            <Heart className="w-4 h-4 fill-red-500/20 text-red-600" />
                          </div>
                          <span className="text-[11px] font-semibold text-slate-500">
                            TFDI Støtte
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Navn og beskrivelse under logoet */}
                    <div>
                      <h3 className="font-bold text-slate-900 text-base group-hover:text-red-600 transition-colors">
                        {sponsor.name}
                      </h3>
                      {sponsor.subtitle && (
                        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                          {sponsor.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Call To Action Box at bottom */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 sm:p-10 rounded-3xl shadow-sm relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mt-12">
          <div className="absolute right-0 bottom-0 translate-x-8 translate-y-8 w-48 h-48 bg-red-600/20 rounded-full blur-2xl pointer-events-none" />
          
          <div className="space-y-2 max-w-2xl relative z-10">
            <div className="flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              <span>Bliv sponsor</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white">
              Vil din virksomhed også støtte sagen?
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              Uanset om I bidrager økonomisk, med produkter eller med faglig ekspertise, gør det en direkte forskel for Danmarks udsendte og beredskabspersonale.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 relative z-10 shrink-0">
            <Link
              to="/samarbejde"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
            >
              <span>Læs om samarbejde</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl text-xs font-bold transition-colors"
            >
              <span>Kontakt os</span>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
